import Button from '@components/Button';
import Icon from '@components/Icon';
import { getEventDetails } from '@store/slices/eventSlice';
import { ROOT_URL } from '@utils/apiHooks';
import { useMobileWidth, useTabletWidth } from '@utils/hooks';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { Ticket } from '..';
// import { InternalTicketModal } from './InternalTicketModal';
import {
  TicketItemWrapper,
  TicketItemContentWrapper,
  TitleText,
  TicketContent,
  LeftColumnTop,
  MobileWrapper,
  GridWrapper,
  MobilePriceVatWrapper,
  Price,
  QuantityWrapper,
  Input,
  SubtitleText,
  FlexContainer,
  ButtonsWrapper,
  LeftColumn,
  RightColumn,
  ErrorText,
} from '../styles';
import { formatCurrency } from '@utils/fn';

interface TicketItemProps {
  ticket: Ticket;
  hasBorderTop?: boolean;
  hasBorderBottom?: boolean;
  hasFullContent: boolean;
}

export const TicketItem = ({
  ticket,
  hasBorderTop,
  hasBorderBottom,
  hasFullContent,
}: TicketItemProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const theme = useTheme();

  const isMobile = useMobileWidth();
  const isTablet = useTabletWidth();
  const smallView = isMobile || isTablet;

  const [hideDiscountRelatedItems, setHideDiscountRelatedItems] =
    useState<boolean>(false);
  const [discountCode, setDiscountCode] = useState<string>('');
  const [discountCodeValidated, setDiscountCodeValidated] =
    useState<boolean>(false);
  const [discountPrice, setDiscountPrice] = useState<number>(-1);
  const [discountErrorMessage, setDiscountErrorMessage] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  // const [isInternalModalOpen, setIsInternalModalOpen] = useState<boolean>(false);

  const handleQuantityChange = (method: 'increase' | 'decrease') => {
    setQuantity(prev =>
      method === 'increase' ? prev + 1 : Math.max(prev - 1, 0)
    );
  };

  const handleDiscountCodeChange = (e: any) => {
    setDiscountCode(e?.target?.value || '');
  };

  const handleDiscountVerifyClick = (discountCode: string) => {
    if (ticket.id && discountCode && sluggedEvent?.id) {
      fetch(
        `${ROOT_URL}/tickets/discount?tickets=${ticket.id}&code=${discountCode}&lang=${i18n.language}&event=${sluggedEvent?.id}`
      )
        .then(async response => await response.json())
        .then(response => {
          const data = response?.result?.data;
          const ticketsArr = data?.tickets ?? [];
          const ticketAssigned = ticketsArr.includes(ticket.id);

          // Check min/max ticket restrictions (0 means no restriction)
          const minTicket = data?.min_ticket ?? 0;
          const maxTicket = data?.max_ticket ?? 0;
          const minTicketValid = minTicket === 0 || quantity >= minTicket;
          const maxTicketValid = maxTicket === 0 || quantity <= maxTicket;

          // Check date restrictions (null means no restriction)
          const validFrom = data?.valid_from;
          const expireAt = data?.expire_at;
          const now = new Date();
          let dateValid = true;
          if (validFrom) {
            dateValid = dateValid && now >= new Date(validFrom);
          }
          if (expireAt) {
            dateValid = dateValid && now <= new Date(expireAt);
          }

          if (
            data &&
            minTicketValid &&
            maxTicketValid &&
            dateValid &&
            ticketAssigned
          ) {
            setDiscountCodeValidated(true);
            setDiscountPrice(ticket.price - ticket.price * (data.value / 100));
            setDiscountErrorMessage('');
          } else {
            setDiscountCodeValidated(false);
            setDiscountPrice(-1);
            let message = `Something went wrong.`;
            if (!minTicketValid) {
              message = `${message} Minimum ticket quantity is set to ${String(minTicket)}.`;
            }
            if (!maxTicketValid) {
              message = `${message} Maximum ticket quantity is set to ${String(maxTicket)}.`;
            }
            if (!dateValid) {
              message = `${message} Discount code is not valid for the current date.`;
            }
            if (!ticketAssigned) {
              message = `${message} Discount code is not assigned to this ticket.`;
            }
            if (response?.status === 404 && response?.message) {
              setDiscountErrorMessage(response?.message);
            } else {
              setDiscountErrorMessage(
                data?.invalid_fields?.code?.[0] ?? message
              );
            }
          }
        })
        .catch(error => {
          console.error('Error verifying discount code:', error);
          setDiscountCodeValidated(false);
          setDiscountPrice(-1);
          setDiscountErrorMessage(
            'Something went wrong. Please try again later.'
          );
        });
    } else {
      console.error('Ticket ID, discount code, or event ID is missing/invalid');
    }
  };

  const handleBuyNowClick = () => {
    // Check if this is an internal ticket
    const isInternalTicket = ticket.external_link?.type === 'none';

    if (isInternalTicket) {
      window.location.href = `${window.location.origin}/events/${sluggedEvent?.slug}${t('book-now-checkout-url')}?ticketId=${ticket.id}&quantity=${quantity}&discountCode=${discountCode}`;
    }

    if (ticket.external_link?.type === 'tito' && !discountCodeValidated) {
      handleDiscountVerifyClick(discountCode);
    } else {
      console.log('TODO: Implement buy now functionality');
    }
  };

  const renderBuyButton = () => {
    if (
      ticket.external_link?.type === 'tito' &&
      (discountCode?.length === 0 || discountCodeValidated)
    ) {
      const { event, releases } = parseUrl(ticket.external_link?.url || '');

      return (
        <tito-widget
          event={event}
          releases={releases}
          discount-code={discountCode ?? ''}
        >
          {t('buy-now-btn')}
        </tito-widget>
      );
    } else {
      return (
        <Button
          onClick={handleBuyNowClick}
          title={t('buy-now-btn')}
          backgroundColor={sluggedEvent?.design.themeColor}
          hoverBackgroundColor={theme.color.mobilityGrey}
          textColor={theme.color.white}
          borderRadius={theme.borderRadius.radius10}
          padding={smallView ? '1.2rem 2.5rem' : '1.35rem 2.5rem'}
          width="100%"
        />
      );
    }
  };

  useEffect(() => {
    if (!wrapperRef.current) return;
    // Find the tito quantity input inside this TicketItem only
    const input: HTMLInputElement | null = wrapperRef.current.querySelector(
      'input.tito-quantity-input'
    );
    if (input) {
      input.value = String(quantity);
      // Optionally trigger input event if Tito listens for it
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, [quantity]);

  useEffect(() => {
    if (ticket.external_link?.type === 'tito') {
      const code = (ticket as any).external_public_discount ?? '';

      if (code.length > 0) {
        setDiscountCode(code);
        handleDiscountVerifyClick(code);
      }
    }
  }, [ticket]);

  const { originalTotal, discountedTotal, showStrikethrough } = useMemo(() => {
    const publicDiscount = Number((ticket as any)?.public_discount ?? 0);
    const hasAutoDiscount = !discountCodeValidated && publicDiscount > 0;

    const unitOriginal = Number(ticket.price ?? 0);
    const unitAutoDiscounted = hasAutoDiscount
      ? Number((unitOriginal * (1 - publicDiscount / 100)).toFixed(2))
      : -1;

    const originalTotal = unitOriginal * quantity;
    const discountedTotal = discountCodeValidated
      ? discountPrice * quantity
      : hasAutoDiscount
        ? unitAutoDiscounted * quantity
        : -1;

    const showStrikethrough = discountCodeValidated || hasAutoDiscount;

    return {
      unitOriginal,
      unitAutoDiscounted,
      originalTotal,
      discountedTotal,
      showStrikethrough,
    };
  }, [ticket, quantity, discountCodeValidated, discountPrice]);

  useEffect(() => {
    if (quantity >= 1 && originalTotal === 0) {
      setHideDiscountRelatedItems(true);
    }
  }, [quantity, originalTotal, discountedTotal]);

  return (
    <TicketItemWrapper
      ref={wrapperRef}
      backgroundColor={sluggedEvent?.design?.themeColor}
      hasBorderTop={hasBorderTop}
      hasBorderBottom={hasBorderBottom}
      hasFullContent={hasFullContent}
    >
      <TicketItemContentWrapper>
        {hasFullContent && <TitleText>{ticket.name}</TitleText>}

        <TicketContent>
          <LeftColumnTop>
            {hasFullContent && (
              <div
                dangerouslySetInnerHTML={{
                  __html: ticket.description
                    .replaceAll('<p><br></p>', '')
                    .replaceAll('<p>', '<li>')
                    .replaceAll('</p>', '</li>')
                    .replaceAll('✓', ''),
                }}
              />
            )}
          </LeftColumnTop>

          {smallView && (
            <MobileWrapper>
              <GridWrapper>
                <MobilePriceVatWrapper>
                  <Price
                    color={sluggedEvent?.design?.themeColor}
                    textDecoration={showStrikethrough ? 'line-through' : 'none'}
                  >
                    {formatCurrency(originalTotal, ticket?.currency)}
                  </Price>

                  {discountedTotal !== -1 && (
                    <Price color={sluggedEvent?.design?.themeColor}>
                      {formatCurrency(discountedTotal, ticket?.currency)}
                    </Price>
                  )}
                </MobilePriceVatWrapper>

                <QuantityWrapper>
                  <Icon
                    name="minus"
                    onClick={() => {
                      handleQuantityChange('decrease');
                    }}
                    color={sluggedEvent?.design?.themeColor}
                    indentLess
                    margin="0 1rem 0 0"
                    size={25}
                  />
                  <Input type="number" value={quantity} />
                  <Icon
                    name="plus"
                    onClick={() => {
                      handleQuantityChange('increase');
                    }}
                    color={sluggedEvent?.design?.themeColor}
                    indentLess
                    margin="0 0 0 1rem"
                    size={25}
                  />
                </QuantityWrapper>
              </GridWrapper>

              {renderBuyButton()}

              {!hideDiscountRelatedItems && (
                <div>
                  <SubtitleText
                    color={
                      !hasFullContent
                        ? sluggedEvent?.design.themeColor
                        : theme.color.mobilityGrey
                    }
                    fontSize={!hasFullContent ? '2.6rem' : undefined}
                    fontWeight={!hasFullContent ? '700' : undefined}
                  >
                    {t('redeem-your-discount-code-here')}
                  </SubtitleText>
                  <FlexContainer>
                    <Input
                      value={discountCode}
                      type="text"
                      placeholder={''}
                      onChange={handleDiscountCodeChange}
                      width="100%"
                      height="auto"
                      padding="1.2rem 2.5rem"
                    />
                    <Button
                      title={t('reedem-now')}
                      backgroundColor={sluggedEvent?.design.themeColor}
                      hoverBackgroundColor={theme.color.mobilityGrey}
                      textColor={theme.color.white}
                      fontSize="1.8rem"
                      borderRadius={theme.borderRadius.radius10}
                      padding="1.2rem 2.4rem"
                      width="100%"
                      onClick={() => {
                        handleDiscountVerifyClick(discountCode);
                      }}
                    />
                  </FlexContainer>
                </div>
              )}
            </MobileWrapper>
          )}
        </TicketContent>

        {!smallView && (
          <ButtonsWrapper>
            <LeftColumn>
              {!hideDiscountRelatedItems && (
                <>
                  <SubtitleText
                    color={
                      !hasFullContent
                        ? sluggedEvent?.design.themeColor
                        : theme.color.mobilityGrey
                    }
                    fontSize={!hasFullContent ? '3rem' : undefined}
                    fontWeight={!hasFullContent ? '700' : undefined}
                  >
                    {t('redeem-your-discount-code-here')}
                  </SubtitleText>
                  <FlexContainer>
                    <Input
                      value={discountCode}
                      type="text"
                      placeholder={''}
                      onChange={handleDiscountCodeChange}
                      width="auto"
                      height="60px"
                      color={
                        discountErrorMessage
                          ? theme.color.error
                          : theme.color.mobilityGrey
                      }
                    />
                    <Button
                      title={t('reedem-now')}
                      backgroundColor={sluggedEvent?.design.themeColor}
                      hoverBackgroundColor={theme.color.mobilityGrey}
                      textColor={theme.color.white}
                      borderRadius={theme.borderRadius.radius10}
                      padding="1.2rem 2.5rem"
                      width="100%"
                      onClick={() => {
                        handleDiscountVerifyClick(discountCode);
                      }}
                    />
                  </FlexContainer>
                </>
              )}
            </LeftColumn>

            <RightColumn>
              <QuantityWrapper>
                <Icon
                  name="minus"
                  onClick={() => {
                    handleQuantityChange('decrease');
                  }}
                  color={sluggedEvent?.design?.themeColor}
                  indentLess
                  margin="0 1rem 0 0"
                  size={25}
                />
                <Input type="number" value={quantity} />
                <Icon
                  name="plus"
                  onClick={() => {
                    handleQuantityChange('increase');
                  }}
                  color={sluggedEvent?.design?.themeColor}
                  indentLess
                  margin="0 0 0 1rem"
                  size={25}
                />
              </QuantityWrapper>

              <Price
                color={sluggedEvent?.design?.themeColor}
                textDecoration={showStrikethrough ? 'line-through' : 'none'}
              >
                {formatCurrency(originalTotal, ticket?.currency)}
              </Price>

              {discountedTotal !== -1 && (
                <Price color={sluggedEvent?.design?.themeColor}>
                  {formatCurrency(discountedTotal, ticket?.currency)}
                </Price>
              )}

              {renderBuyButton()}
            </RightColumn>
          </ButtonsWrapper>
        )}
      </TicketItemContentWrapper>

      {discountErrorMessage && <ErrorText>{discountErrorMessage}</ErrorText>}

      {/* Internal Ticket Modal */}
      {/*
      <InternalTicketModal
        ticket={ticket}
        isOpen={isInternalModalOpen}
        onClose={() => {
          setIsInternalModalOpen(false);
        }}
        quantity={quantity}
        discountCode={discountCodeValidated ? discountCode : undefined}
      />
      */}
    </TicketItemWrapper>
  );
};

function parseUrl(url: string) {
  // Remove leading slash if present
  if (url.startsWith('/')) {
    url = url.slice(1);
  }

  const withIndex = url.indexOf('/with');

  if (withIndex === -1) {
    return { event: url, releases: '' };
  }

  const event = url.slice(0, withIndex);
  let releases = '';

  // Check if there's anything after "/with"
  if (url.startsWith('/with/', withIndex)) {
    releases = url.slice(withIndex + 6); // skip "/with/"
  }

  return { event, releases };
}
